import React, { useMemo, useState } from 'react';
import 'react-alice-carousel/lib/alice-carousel.css';
import { Typography, Box, Grid } from '@mui/material';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import Markdown from 'markdown-to-jsx';
import Head from 'next/head';
import Image from 'next/image';
import { getUserFromStorage } from '../../utils/index';
import TickmarkCircle from '../../icons/TickmarkCircle';
import TickMarkCircleWhiteBold from '../../icons/TickMarkCircleWhiteBold';
import { NextImage } from '../../components/NextImage/index';
import { Button, LibButtonProps } from '../../components/Button';
import markdownOverrides from '../../utils/MarkdownOverrides';

import {
  getBgStyles,
  getLayoutTextStyles,
  getTitleTextStyles,
  getSubtitleStyles,
  getDescriptionStyles,
  getBulletTextStyles
} from './utility';
import {
  bindComponentOnClickHandler,
  getMyFoxtonsPortalUrl
} from '../../utils';
import styles from './styles';
import { fallbackImageUrl } from '../../utils/Constants/fallbackImageUrl';

const AliceCarousel = dynamic(import('react-alice-carousel'), { ssr: false });

// TODO: refactor props
export interface LibLibGetAheadProps {
  sectionIndex?: number;
  title?: string;
  subtitle?: string;
  description?: string;
  italicText?: string;
  benefits?: Array<{ id: string; text: string }>;
  btnTextPrimary?: string;
  btnPrimaryLink?: string;
  btnPrimaryTarget?: string;
  btnPrimaryVariant?: LibButtonProps['variant'];
  handleClickPrimary?: () => void;
  btnTextSecondary?: string;
  btnSecondaryLink?: string;
  btnSecondaryTarget?: string;
  btnSecondaryVariant?: LibButtonProps['variant'];
  handleClickSecondary?: () => void;
  richTextContent?: string;
  bgImg: string;
  bgImgAltText?: string;
  logoImg?: {
    img: string;
    altText: string;
  };
  bgImgList?: {
    img: string;
    altText: string;
    smallImg: string;
  }[];
  vimeoURL?: string;
  qrImg?: {
    imgList: {
      img: string;
      altText: string;
    }[];
    msgText?: string;
    isVisible?: boolean;
  };
  isMultipleImges?: boolean;
  smallBgImg?: string;
  variant?:
    | 'base'
    | 'normalLeft'
    | 'normalLeftExtreme'
    | 'reverseLeft'
    | 'reverseCenter'
    | 'normalCenterAqua'
    | 'reverseCenterAqua'
    | 'normalCenterGreen'
    | 'alexanderHall'
    | 'redGradient';
}

function LibGetAhead({
  sectionIndex,
  title,
  subtitle,
  italicText,
  description,
  benefits,
  btnTextPrimary,
  btnPrimaryLink,
  btnPrimaryTarget,
  btnPrimaryVariant,
  handleClickPrimary,
  btnTextSecondary,
  btnSecondaryLink,
  btnSecondaryTarget,
  btnSecondaryVariant,
  handleClickSecondary,
  richTextContent,
  bgImg,
  bgImgAltText,
  logoImg,
  vimeoURL,
  qrImg,
  bgImgList,
  smallBgImg,
  isMultipleImges = false,
  variant = 'base'
}: LibLibGetAheadProps) {
  const overrides = {
    ...markdownOverrides,
    h1: {
      component: Typography,
      props: { variant: 'h1', sx: getTitleTextStyles(variant) }
    },
    h2: {
      component: Typography,
      props: { variant: 'h2', sx: getTitleTextStyles(variant) }
    },
    h3: {
      component: Typography,
      props: { variant: 'h3', sx: getSubtitleStyles(variant) }
    },
    h4: {
      component: Typography,
      props: { variant: 'h4', sx: styles.italicText }
    },
    h5: {
      component: Typography,
      props: { variant: 'h5', sx: styles.centerHeading }
    },
    h6: {
      component: Typography,
      props: { variant: 'h6', sx: styles.centerHeadingTwo }
    },
    p: {
      component: Typography,
      props: {
        variant: 'body1',
        sx: styles.italicText
      }
    },
    a: {
      component: (props) =>
        markdownOverrides.a.component({
          ...props,
          style: markdownOverrides.a.props.style,
          className: props.href.indexOf('tel:') === 0 ? 'hyper-link' : undefined
        })
    },
    ul: {
      ...markdownOverrides.ul,
      props: {
        ...markdownOverrides.ul.props,
        mb: 1
      }
    }
  };

  const [hasError, setHasError] = useState(false);

  const scrollHandle = (id: string) => {
    const element = document.getElementById(id.replace('#', ''))?.parentElement; // heading parent section

    const headerOffset = 200;
    const elementPosition = element?.getBoundingClientRect().top;
    const destination = elementPosition + window.pageYOffset - headerOffset;

    if (destination) window.scrollTo({ behavior: 'smooth', top: destination });
  };

  const { user } = getUserFromStorage();

  const userLoggedIn = useMemo(() => !!user?.isLoggedIn, [user]);

  const convertRegistrationLinkLoggedInUser = (linkHref: string) => {
    if (userLoggedIn && linkHref === '/auth/registration') {
      return getMyFoxtonsPortalUrl();
    }

    return linkHref;
  };

  const convertRegistrationButtonTextLoggedInUser = (buttonLabel: string) => {
    if (userLoggedIn && buttonLabel === 'Register') {
      return 'Go To My Foxtons';
    }

    return buttonLabel;
  };

  const handleOpen = () => {
    const componentName = btnPrimaryLink.replace(/component:/, '');

    if (componentName === 'SearchPropertyBox-longLet') {
      const componentSplitName = componentName.split('-');

      sessionStorage.setItem('showSearchWidget', 'true');
      sessionStorage.setItem('searchType', componentSplitName[1]);
      window.dispatchEvent(new Event('storage'));
    } else {
      bindComponentOnClickHandler(btnPrimaryLink);
    }
  };

  const isCloseToTheViewport = sectionIndex <= 1;

  /* eslint react/jsx-no-useless-fragment: 0 */
  // Rob - Fix this so the fragment works with variant alexanderhall
  return (
    <>
      <Grid container sx={styles[variant]}>
        <Grid item xs={12} lg={6}>
          {!isMultipleImges && (
            <Box sx={getBgStyles(variant)}>
              {/* If vimeoURL prop is set, the Image url will be ignored */}
              {vimeoURL ? (
                <Box sx={styles.vimeoContainer}>
                  <iframe
                    width="100%"
                    height="100%"
                    title="GetAhead Video"
                    style={{ border: 0 }}
                    src={vimeoURL}
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                  />
                </Box>
              ) : (
                <>
                  <Head>
                    <link
                      rel="preload"
                      href={smallBgImg}
                      as="image"
                      media="(max-width: 599px)"
                      fetchPriority="high"
                    />
                    <link
                      rel="preload"
                      href={bgImg}
                      as="image"
                      media="(min-width: 1200px)"
                      fetchPriority="high"
                    />
                  </Head>

                  <picture>
                    <source
                      srcSet={hasError ? fallbackImageUrl : bgImg || ''}
                      media="(min-width: 1200px)"
                    />
                    <Image
                      src={hasError ? fallbackImageUrl : smallBgImg || ''}
                      alt={bgImgAltText}
                      style={{ width: '100%', height: '100%' }}
                      sizes="100vw"
                      width={0}
                      height={0}
                      priority
                      unoptimized
                      onError={() => setHasError(true)}
                    />
                  </picture>
                </>
              )}
            </Box>
          )}
          {isMultipleImges && (
            <Box>
              <AliceCarousel
                mouseTracking
                disableButtonsControls
                disableDotsControls
                touchTracking
                autoPlay
                infinite
                autoPlayInterval={3000}
                items={bgImgList.map((imgList) => (
                  <Box key={imgList?.img} sx={getBgStyles(variant)}>
                    <NextImage
                      priority={isCloseToTheViewport}
                      loading={isCloseToTheViewport ? 'eager' : 'lazy'}
                      image={{
                        src: imgList.img,
                        alt: imgList.altText || 'alt-text'
                      }}
                      style={{ width: '100%', height: '100%' }}
                      sizes="50vw"
                    />
                  </Box>
                ))}
                ssrSilentMode
              />
            </Box>
          )}
        </Grid>
        <Grid item xs={12} lg={6} sx={getLayoutTextStyles(variant)}>
          {title && (
            <Typography variant="h2" sx={getTitleTextStyles(variant)}>
              {title}
            </Typography>
          )}
          {description && (
            <Typography variant="h3" sx={getDescriptionStyles(variant)}>
              {description}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="h3" sx={getSubtitleStyles(variant)}>
              {subtitle}
            </Typography>
          )}
          {italicText && (
            <Typography variant="h4" sx={styles.italicText}>
              {italicText}
            </Typography>
          )}
          {richTextContent && (
            <Markdown
              options={{ overrides }}
              style={styles.contentBox}
              className="contentBox"
            >
              {richTextContent}
            </Markdown>
          )}
          {benefits && benefits.length > 0 && (
            <Box sx={styles.bulletPointsContainer}>
              {benefits.map(({ text, id }) => (
                <Box sx={styles.bulletPointBox} key={id}>
                  {variant === 'normalCenterGreen' ? (
                    <TickMarkCircleWhiteBold sx={styles.iconTickStyle} />
                  ) : (
                    <TickmarkCircle sx={styles.iconTickStyle} />
                  )}
                  <Typography sx={getBulletTextStyles(variant)}>
                    {text}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          <Box sx={styles.btnBox} id="btnBox">
            {btnPrimaryLink?.includes('component:') ? (
              <Button
                color="primary"
                variant={btnPrimaryVariant || 'primary-dark'}
                sx={styles.btnSecondary}
                href={btnPrimaryLink}
                onClick={
                  btnPrimaryLink?.match(/^component:SearchPropertyBox-\w+$/)
                    ? handleOpen
                    : null
                }
              >
                {btnTextPrimary}
              </Button>
            ) : null}

            {btnTextPrimary && !btnPrimaryLink?.includes('component:') && (
              <Box>
                {btnPrimaryLink && btnPrimaryLink?.indexOf('#') !== 0 ? (
                  <Link
                    href={convertRegistrationLinkLoggedInUser(btnPrimaryLink)}
                    prefetch={false}
                    target={btnPrimaryTarget || '_self'}
                  >
                    <Button
                      color="primary"
                      variant={btnPrimaryVariant || 'primary-dark'}
                      sx={styles.btnSecondary}
                    >
                      {convertRegistrationButtonTextLoggedInUser(
                        btnTextPrimary
                      )}
                    </Button>
                  </Link>
                ) : (
                  <Button
                    color="primary"
                    variant={btnPrimaryVariant || 'primary-dark'}
                    sx={styles.btnSecondary}
                    isNotButton={!handleClickPrimary && !btnPrimaryLink}
                    onClick={
                      btnPrimaryLink?.indexOf('#') === 0
                        ? () => scrollHandle(btnPrimaryLink)
                        : handleClickPrimary
                    }
                  >
                    {btnTextPrimary}
                  </Button>
                )}
              </Box>
            )}

            {btnTextSecondary && (
              <Box>
                {btnSecondaryLink ? (
                  <Link
                    href={btnSecondaryLink}
                    prefetch={false}
                    target={btnSecondaryTarget || '_self'}
                  >
                    <Button
                      color="primary"
                      variant={btnSecondaryVariant || 'primary-dark'}
                      sx={styles.btnSecondary}
                    >
                      {btnTextSecondary}
                    </Button>
                  </Link>
                ) : (
                  <Button
                    color="primary"
                    variant={btnSecondaryVariant || 'primary-dark'}
                    sx={styles.btnSecondary}
                    onClick={handleClickSecondary}
                  >
                    {btnTextSecondary}
                  </Button>
                )}
              </Box>
            )}
          </Box>
          {/* QR code block (optional) */}
          {Boolean(qrImg?.isVisible) && (
            <Box id="qr-box" sx={styles.qrContainer}>
              {qrImg?.msgText && (
                <Box>
                  <Typography variant="bold" sx={styles.qrText}>
                    {qrImg?.msgText}
                  </Typography>
                </Box>
              )}
              <Box sx={styles.qrImgBox}>
                {qrImg?.imgList?.map((imgList) => (
                  <div key={imgList?.img}>
                    <NextImage
                      image={{ src: imgList?.img, alt: imgList?.altText || '' }}
                      style={{ width: '8.75rem', height: '8.75rem' }}
                      sizes="30vw"
                    />
                  </div>
                ))}
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
      {variant === 'alexanderHall' ? (
        <Box sx={styles.alexanderHallBox}>
          <NextImage
            image={{ src: logoImg?.img, alt: logoImg?.altText }}
            style={{ width: '100%', height: '100%' }}
            sizes="100vw"
          />
        </Box>
      ) : null}
    </>
  );
}

export const GetAhead = React.memo(LibGetAhead);

export default GetAhead;
